// src/pages/task/views/CalendarView.tsx
// Monthly / weekly calendar. Tasks sit on the day of their scheduled work
// block, or on their due date when they are not scheduled.
//
// Interactive: chips can be dragged to another day, an empty day opens an
// inline quick-create, and the "+N more" affordance opens the full day.

import { useCallback, useMemo, useRef, useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Button } from '@react/components';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';

import type { Task, TaskStatusDefinition } from '@core/types/task';

import { getEntityId } from '@/features/task/logic';

import { AnchoredPopover } from './AnchoredPopover';
import {
  addDays,
  dateKey,
  formatDayCellHeader,
  formatMonthYear,
  formatShortDate,
  formatTimeRange,
  getMonthGrid,
  getPlannedDate,
  getScheduledRange,
  getWeekDays,
  resolveStatusStyle,
  weekdayNames,
  type StatusStyle,
} from './planning';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CalendarViewProps {
  tasks: Task[];
  statusDefinitions?: TaskStatusDefinition[];
  onTaskClick: (id: string) => void;
  /** Drop a task on another day. Resolves once the server accepted (or not). */
  onTaskDrop?: (taskId: string, date: Date) => void | Promise<void>;
  /** Inline quick-create on a day; the new task is due that day. */
  onQuickCreate?: (date: Date, title: string) => void | Promise<void>;
}

type CalendarMode = 'month' | 'week';

/** Chips shown before the day collapses into "+N more". */
const VISIBLE_CHIPS: Record<CalendarMode, number> = { month: 3, week: 8 };

interface DayEntry {
  id: string;
  task: Task;
  title: string;
  style: StatusStyle;
  timeLabel: string;
  /** Start of the work block, so chips order by clock time, not by label. */
  sortKey: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CalendarView = observer(({
  tasks,
  statusDefinitions,
  onTaskClick,
  onTaskDrop,
  onQuickCreate,
}: CalendarViewProps) => {
  const { t, locale } = useI18n();
  const today = useMemo(() => new Date(), []);

  const [mode, setMode] = useState<CalendarMode>('month');
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [weekBase, setWeekBase] = useState<Date>(today);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [popover, setPopover] = useState<
    { kind: 'more' | 'create'; key: string; date: Date; anchor: DOMRect } | null
  >(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const days = useMemo(
    () => (mode === 'month' ? getMonthGrid(currentYear, currentMonth) : getWeekDays(weekBase)),
    [mode, currentYear, currentMonth, weekBase],
  );

  // Tasks bucketed on the day they are planned for: scheduled start when the
  // task has a work block, due date otherwise.
  const entriesByDay = useMemo(() => {
    const map = new Map<string, DayEntry[]>();
    for (const task of tasks) {
      const planned = getPlannedDate(task);
      if (!planned) continue;
      const key = dateKey(planned);
      const range = getScheduledRange(task);
      const entry: DayEntry = {
        id: getEntityId(task),
        task,
        title: (task as unknown as { title?: string }).title || t('common.untitled'),
        style: resolveStatusStyle(task, statusDefinitions),
        timeLabel: range && !range.isAllDay ? formatTimeRange(range.start, range.end, locale) : '',
        sortKey: range && !range.isAllDay ? range.start.getTime() : Number.NEGATIVE_INFINITY,
      };
      const bucket = map.get(key);
      if (bucket) bucket.push(entry);
      else map.set(key, [entry]);
    }
    for (const bucket of map.values()) {
      bucket.sort((a, b) => a.sortKey - b.sortKey || a.title.localeCompare(b.title));
    }
    return map;
  }, [tasks, statusDefinitions, locale, t]);

  const dayNames = useMemo(() => weekdayNames(locale), [locale]);
  const todayKey = dateKey(today);

  const handlePrev = useCallback(() => {
    if (mode === 'month') {
      setCurrentMonth((month) => {
        if (month === 0) {
          setCurrentYear((year) => year - 1);
          return 11;
        }
        return month - 1;
      });
    } else {
      setWeekBase((base) => addDays(base, -7));
    }
  }, [mode]);

  const handleNext = useCallback(() => {
    if (mode === 'month') {
      setCurrentMonth((month) => {
        if (month === 11) {
          setCurrentYear((year) => year + 1);
          return 0;
        }
        return month + 1;
      });
    } else {
      setWeekBase((base) => addDays(base, 7));
    }
  }, [mode]);

  const handleToday = useCallback(() => {
    const now = new Date();
    setCurrentYear(now.getFullYear());
    setCurrentMonth(now.getMonth());
    setWeekBase(now);
  }, []);

  const activeEntry = useMemo(() => {
    if (!activeId) return null;
    for (const bucket of entriesByDay.values()) {
      const found = bucket.find((entry) => entry.id === activeId);
      if (found) return found;
    }
    return null;
  }, [activeId, entriesByDay]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setPopover(null);
    setActiveId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null);
      const { active, over } = event;
      if (!over || !onTaskDrop) return;

      const targetKey = String(over.id).replace(/^day:/, '');
      const taskId = String(active.id);
      const sourceKey = [...entriesByDay.entries()].find(([, bucket]) =>
        bucket.some((entry) => entry.id === taskId),
      )?.[0];
      if (!targetKey || targetKey === sourceKey) return;

      const target = days.find((day) => dateKey(day) === targetKey);
      if (target) void onTaskDrop(taskId, target);
    },
    [days, entriesByDay, onTaskDrop],
  );

  const openPopover = useCallback(
    (kind: 'more' | 'create', day: Date, element: HTMLElement) => {
      setPopover({ kind, key: dateKey(day), date: day, anchor: element.getBoundingClientRect() });
    },
    [],
  );

  const closePopover = useCallback(() => setPopover(null), []);

  const popoverEntries = popover ? entriesByDay.get(popover.key) ?? [] : [];

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1">
          {(['month', 'week'] as CalendarMode[]).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setMode(value)}
              aria-pressed={mode === value}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                mode === value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {t(`workspace.calendar.${value}`)}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={handlePrev} aria-label={t('common.previous')}>
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Button>
          <span className="text-sm font-semibold text-foreground min-w-[180px] text-center">
            {mode === 'month'
              ? formatMonthYear(new Date(currentYear, currentMonth, 1), locale)
              : `${t('workspace.calendar.weekOf')} ${formatShortDate(days[0], locale)}`}
          </span>
          <Button variant="ghost" size="sm" onClick={handleNext} aria-label={t('common.next')}>
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Button>
          <Button variant="secondary" size="sm" onClick={handleToday}>
            {t('workspace.calendar.today')}
          </Button>
        </div>

        <p className="text-[11px] text-muted-foreground">
          {onTaskDrop ? t('task.planning.calendarHint') : ''}
        </p>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveId(null)}
      >
        <div className="rounded-lg border border-border overflow-hidden">
          {/* Weekday header */}
          <div className="grid grid-cols-7 bg-muted/30">
            {dayNames.map((name) => (
              <div
                key={name}
                className="text-center text-xs font-semibold text-muted-foreground py-2 border-b border-border"
              >
                {name}
              </div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7">
            {days.map((day) => {
              const key = dateKey(day);
              return (
                <DayCell
                  key={key}
                  day={day}
                  dayKey={key}
                  entries={entriesByDay.get(key) ?? []}
                  maxChips={VISIBLE_CHIPS[mode]}
                  dimmed={mode === 'month' && day.getMonth() !== currentMonth}
                  isToday={key === todayKey}
                  draggable={!!onTaskDrop}
                  canCreate={!!onQuickCreate}
                  activeId={activeId}
                  onTaskClick={onTaskClick}
                  onShowMore={(element) => openPopover('more', day, element)}
                  onCreate={(element) => openPopover('create', day, element)}
                />
              );
            })}
          </div>
        </div>

        <DragOverlay>
          {activeEntry && (
            <div
              className={`rounded px-1.5 py-0.5 text-[10px] font-medium text-white shadow-lg ${activeEntry.style.className}`}
              style={activeEntry.style.hex ? { backgroundColor: activeEntry.style.hex } : undefined}
            >
              {activeEntry.title}
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {/* Day panel / quick-create */}
      {popover?.kind === 'more' && (
        <AnchoredPopover
          anchor={popover.anchor}
          onClose={closePopover}
          label={formatDayCellHeader(popover.date, locale)}
          width={280}
        >
          <p className="px-1 pb-1 text-xs font-semibold text-foreground">
            {formatDayCellHeader(popover.date, locale)}
          </p>
          <ul className="max-h-64 space-y-1 overflow-y-auto">
            {popoverEntries.map((entry) => (
              <li key={entry.id}>
                <button
                  type="button"
                  onClick={() => {
                    closePopover();
                    onTaskClick(entry.id);
                  }}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs text-foreground hover:bg-muted"
                >
                  <span
                    className={`h-2 w-2 flex-shrink-0 rounded-full ${entry.style.className}`}
                    style={entry.style.hex ? { backgroundColor: entry.style.hex } : undefined}
                  />
                  <span className="flex-1 truncate">{entry.title}</span>
                  {entry.timeLabel && (
                    <span className="text-[10px] text-muted-foreground">{entry.timeLabel}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
          {onQuickCreate && (
            <div className="mt-1 border-t border-border pt-1">
              <QuickCreateForm
                date={popover.date}
                onSubmit={onQuickCreate}
                onDone={closePopover}
              />
            </div>
          )}
        </AnchoredPopover>
      )}

      {popover?.kind === 'create' && onQuickCreate && (
        <AnchoredPopover
          anchor={popover.anchor}
          onClose={closePopover}
          label={t('task.planning.createOnDay', { date: formatDayCellHeader(popover.date, locale) })}
          width={260}
        >
          <p className="px-1 pb-1 text-xs font-semibold text-foreground">
            {t('task.planning.createOnDay', { date: formatDayCellHeader(popover.date, locale) })}
          </p>
          <QuickCreateForm date={popover.date} onSubmit={onQuickCreate} onDone={closePopover} />
        </AnchoredPopover>
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Quick create
// ---------------------------------------------------------------------------

interface QuickCreateFormProps {
  date: Date;
  onSubmit: (date: Date, title: string) => void | Promise<void>;
  onDone: () => void;
}

function QuickCreateForm({ date, onSubmit, onDone }: QuickCreateFormProps) {
  const { t } = useI18n();
  const [value, setValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <form
      className="flex gap-1"
      onSubmit={async (event) => {
        event.preventDefault();
        const title = value.trim();
        if (!title || isSaving) return;
        setIsSaving(true);
        try {
          await onSubmit(date, title);
          setValue('');
          onDone();
        } finally {
          setIsSaving(false);
        }
      }}
    >
      <input
        ref={inputRef}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        type="text"
        value={value}
        disabled={isSaving}
        onChange={(event) => setValue(event.target.value)}
        placeholder={t('task.planning.titlePlaceholder')}
        aria-label={t('task.planning.titlePlaceholder')}
        className="flex-1 rounded border border-border bg-background px-2 py-1 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
      />
      <button
        type="submit"
        disabled={!value.trim() || isSaving}
        className="rounded bg-primary px-2 py-1 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
      >
        {isSaving ? t('common.loading') : t('common.add')}
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Day cell
// ---------------------------------------------------------------------------

interface DayCellProps {
  day: Date;
  dayKey: string;
  entries: DayEntry[];
  maxChips: number;
  dimmed: boolean;
  isToday: boolean;
  draggable: boolean;
  canCreate: boolean;
  activeId: string | null;
  onTaskClick: (id: string) => void;
  onShowMore: (element: HTMLElement) => void;
  onCreate: (element: HTMLElement) => void;
}

function DayCell({
  day,
  dayKey,
  entries,
  maxChips,
  dimmed,
  isToday,
  draggable,
  canCreate,
  activeId,
  onTaskClick,
  onShowMore,
  onCreate,
}: DayCellProps) {
  const { t, locale } = useI18n();
  const { setNodeRef, isOver } = useDroppable({ id: `day:${dayKey}`, disabled: !draggable });
  const hidden = Math.max(0, entries.length - maxChips);

  return (
    <div
      ref={setNodeRef}
      className={`flex min-h-[104px] flex-col gap-1 border-b border-r border-border p-1.5 transition-colors ${
        isToday ? 'bg-primary/5' : ''
      } ${dimmed ? 'opacity-40' : ''} ${isOver && activeId ? 'ring-2 ring-inset ring-primary/50' : ''}`}
    >
      <div className="flex items-center justify-between">
        <span
          className={`text-xs font-medium ${
            isToday
              ? 'inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground'
              : 'text-muted-foreground'
          }`}
        >
          {day.getDate()}
        </span>
        {entries.length > 0 && (
          <span className="text-[10px] text-muted-foreground">{entries.length}</span>
        )}
      </div>

      <div className="space-y-0.5">
        {entries.slice(0, maxChips).map((entry) => (
          <TaskChip
            key={entry.id}
            entry={entry}
            draggable={draggable}
            isDragging={activeId === entry.id}
            onClick={onTaskClick}
          />
        ))}
      </div>

      {hidden > 0 && (
        <button
          type="button"
          onClick={(event) => onShowMore(event.currentTarget)}
          className="rounded px-1 text-left text-[10px] font-medium text-primary hover:underline"
        >
          {t('task.planning.showMore', { count: hidden })}
        </button>
      )}

      {/* Empty area of the day opens quick-create. Rendered as a real control
          so it is reachable by keyboard, not as a click handler on the cell. */}
      {canCreate && (
        <button
          type="button"
          onClick={(event) => onCreate(event.currentTarget)}
          aria-label={t('task.planning.createOnDay', { date: formatDayCellHeader(day, locale) })}
          className="group min-h-[16px] flex-1 rounded text-left transition-colors hover:bg-muted/60 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
        >
          <span className="px-1 text-[10px] text-transparent transition-colors group-hover:text-muted-foreground group-focus-visible:text-muted-foreground">
            {t('task.planning.addShort')}
          </span>
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Task chip
// ---------------------------------------------------------------------------

interface TaskChipProps {
  entry: DayEntry;
  draggable: boolean;
  isDragging: boolean;
  onClick: (id: string) => void;
}

function TaskChip({ entry, draggable, isDragging, onClick }: TaskChipProps) {
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: entry.id,
    disabled: !draggable,
  });

  return (
    <button
      ref={setNodeRef}
      type="button"
      {...attributes}
      {...listeners}
      onClick={() => onClick(entry.id)}
      title={entry.timeLabel ? `${entry.timeLabel} · ${entry.title}` : entry.title}
      style={entry.style.hex ? { backgroundColor: entry.style.hex } : undefined}
      className={`block w-full truncate rounded px-1.5 py-0.5 text-left text-[10px] font-medium text-white transition-opacity hover:opacity-80 ${
        entry.style.className
      } ${isDragging ? 'opacity-40' : ''} ${draggable ? 'cursor-grab active:cursor-grabbing' : ''}`}
    >
      {entry.timeLabel && <span className="mr-1 opacity-80">{entry.timeLabel}</span>}
      {entry.title}
    </button>
  );
}
